/**
 * SmartCHG profile editor.
 *
 * Renders the read-only block (id/email/role/department/employeeId/permissions)
 * plus an editable block (displayName, preferredLanguage, defaultDatasourceId)
 * backed by:
 *   GET   /api/users/profile     — populates the form
 *   PATCH /api/users/profile     — persists displayName/preferredLanguage/
 *                                   defaultDatasourceId (CLAUDE.md C3)
 *   GET   /api/v1/datasources    — populates the default-datasource select
 *                                   only when smart_insights_bot.access is present
 *
 * Auth is Microsoft Entra SSO (CLAUDE.md §3.5) so there is no password-change
 * UI in this module.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle, Save, ShieldCheck } from 'lucide-react';

import {
  userAPI,
  datasourceAPI,
  ProfileUpdatePayload,
  DatasourceSummary,
} from '../../services/api';
import { DEFAULT_SMARTCHG_ROLE } from '../../types';
import { logInfo, logError } from '../../utils/logger';

interface SmartCHGProfile {
  id: string;
  name?: string;
  email: string;
  role: string;
  department: string | null;
  officeLocation: string | null;
  employeeId: string | null;
  permissions: string[];
  displayName: string | null;
  preferredLanguage: string | null;
  defaultDatasourceId: string | null;
}

interface FormState {
  displayName: string;
  preferredLanguage: string;
  defaultDatasourceId: string;
}

const SUPPORTED_LANGUAGES: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
];

const NONE_DATASOURCE_VALUE = '__none__';
const SMART_INSIGHTS_ACCESS_PERMISSION = 'smart_insights_bot.access';

function asArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function hasSmartInsightsAccess(profile: SmartCHGProfile): boolean {
  return (
    profile.role === 'super_admin' ||
    profile.permissions.includes(SMART_INSIGHTS_ACCESS_PERMISSION)
  );
}

function normalizeProfile(raw: unknown): SmartCHGProfile | null {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  if (typeof p.id !== 'string' || typeof p.email !== 'string') return null;
  return {
    id: p.id,
    name: typeof p.name === 'string' ? p.name : undefined,
    email: p.email,
    role: typeof p.role === 'string' ? p.role : DEFAULT_SMARTCHG_ROLE,
    department: typeof p.department === 'string' ? p.department : null,
    officeLocation: typeof p.officeLocation === 'string' ? p.officeLocation : null,
    employeeId: typeof p.employeeId === 'string' ? p.employeeId : null,
    permissions: asArray(p.permissions),
    displayName: typeof p.displayName === 'string' ? p.displayName : null,
    preferredLanguage: typeof p.preferredLanguage === 'string' ? p.preferredLanguage : null,
    defaultDatasourceId: typeof p.defaultDatasourceId === 'string' ? p.defaultDatasourceId : null,
  };
}

export const ComprehensiveProfileEditor: React.FC = () => {
  const [profile, setProfile] = useState<SmartCHGProfile | null>(null);
  const [datasources, setDatasources] = useState<DatasourceSummary[]>([]);
  const [form, setForm] = useState<FormState>({
    displayName: '',
    preferredLanguage: 'en',
    defaultDatasourceId: NONE_DATASOURCE_VALUE,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const applyProfileToForm = useCallback((p: SmartCHGProfile) => {
    setForm({
      displayName: p.displayName ?? '',
      preferredLanguage: p.preferredLanguage ?? 'en',
      defaultDatasourceId: p.defaultDatasourceId ?? NONE_DATASOURCE_VALUE,
    });
  }, []);

  const loadProfile = useCallback(async (): Promise<SmartCHGProfile | null> => {
    try {
      logInfo('Profile', 'Loading SmartCHG profile');
      const response = await userAPI.getProfile();
      const normalized = normalizeProfile(response.data);
      if (!normalized) {
        throw new Error('Invalid profile response');
      }
      setProfile(normalized);
      applyProfileToForm(normalized);
      logInfo('Profile', 'Profile loaded', {
        userId: normalized.id,
        permissionsCount: normalized.permissions.length,
      });
      return normalized;
    } catch (err) {
      logError(
        'Profile',
        'Failed to load profile',
        err instanceof Error ? err : new Error(String(err)),
      );
      const message =
        (err as { response?: { data?: { message?: string; detail?: string } } })?.response?.data
          ?.message ??
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        (err instanceof Error ? err.message : 'Failed to load profile');
      setError(message);
      return null;
    }
  }, [applyProfileToForm]);

  const loadDatasources = useCallback(async (profileWithPermissions: SmartCHGProfile): Promise<void> => {
    if (!hasSmartInsightsAccess(profileWithPermissions)) {
      setDatasources([]);
      return;
    }

    try {
      const response = await datasourceAPI.list();
      const list = Array.isArray(response.data) ? response.data : [];
      setDatasources(list);
    } catch (err) {
      // The select gracefully degrades to "None" when this fails — log only.
      logError(
        'Profile',
        'Failed to load datasources',
        err instanceof Error ? err : new Error(String(err)),
      );
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    loadProfile().then((loadedProfile) => {
      if (loadedProfile) {
        return loadDatasources(loadedProfile);
      }
      setDatasources([]);
      return undefined;
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [loadProfile, loadDatasources]);

  const canUseDefaultDatasource = profile ? hasSmartInsightsAccess(profile) : false;

  const isDirty = useMemo(() => {
    if (!profile) return false;
    const formDsId =
      form.defaultDatasourceId === NONE_DATASOURCE_VALUE ? null : form.defaultDatasourceId;
    const datasourceChanged =
      canUseDefaultDatasource && formDsId !== (profile.defaultDatasourceId || null);

    return (
      (form.displayName || null) !== (profile.displayName || null) ||
      (form.preferredLanguage || null) !== (profile.preferredLanguage || null) ||
      datasourceChanged
    );
  }, [profile, form, canUseDefaultDatasource]);

  const handleSave = useCallback(async () => {
    if (!profile || !isDirty) return;

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    const payload: ProfileUpdatePayload = {
      displayName: form.displayName.trim() || null,
      preferredLanguage: form.preferredLanguage || null,
    };

    if (canUseDefaultDatasource) {
      payload.defaultDatasourceId =
        form.defaultDatasourceId === NONE_DATASOURCE_VALUE ? null : form.defaultDatasourceId;
    }

    try {
      logInfo('Profile', 'Saving profile preferences', { userId: profile.id });
      const response = await userAPI.updateProfile(payload);
      const updated = normalizeProfile(response.data);
      if (updated) {
        setProfile(updated);
        applyProfileToForm(updated);
      } else {
        // Backend returned 200 but body wasn't a profile — refetch.
        await loadProfile();
      }
      setSuccess('Profile saved.');
      window.setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      logError(
        'Profile',
        'Failed to save profile',
        err instanceof Error ? err : new Error(String(err)),
      );
      const message =
        (err as { response?: { data?: { message?: string; detail?: string } } })?.response?.data
          ?.message ??
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        (err instanceof Error ? err.message : 'Failed to save profile');
      setError(message);
    } finally {
      setIsSaving(false);
    }
  }, [profile, form, isDirty, canUseDefaultDatasource, applyProfileToForm, loadProfile]);

  const handleResetForm = useCallback(() => {
    if (profile) applyProfileToForm(profile);
    setError(null);
    setSuccess(null);
  }, [profile, applyProfileToForm]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600" />
        <span className="ml-3 text-slate-600">Loading profile...</span>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center gap-2 text-red-700">
          <AlertCircle className="w-5 h-5" />
          <span>{error ?? 'Failed to load profile information'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-800">Profile</h2>
        <p className="text-slate-600 mt-1">
          Manage your SmartCHG display preferences. Account fields are managed in Microsoft Entra
          and shown here for reference.
        </p>
      </div>

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle className="w-5 h-5" />
            <span>{success}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Account information</h3>
          <dl className="space-y-3 text-sm">
            <ReadOnlyField label="User ID" value={profile.id} />
            <ReadOnlyField label="Email" value={profile.email} />
            <ReadOnlyField
              label="Role"
              value={prettifyRole(profile.role)}
              hint="Set in Microsoft Entra by an administrator."
            />
            <ReadOnlyField
              label="Department"
              value={profile.department ?? 'Not set'}
            />
            <ReadOnlyField
              label="Office Location"
              value={profile.officeLocation ?? 'Not set'}
            />
            <ReadOnlyField
              label="Employee ID"
              value={profile.employeeId ?? 'Not set'}
            />
          </dl>
        </section>

        <section className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            Permissions
          </h3>
          {profile.permissions.length === 0 ? (
            <p className="text-sm text-slate-500">
              No permissions granted yet. Reach out to your administrator if you expect access.
            </p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {profile.permissions.map((perm) => (
                <li
                  key={perm}
                  className="px-2.5 py-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-mono"
                >
                  {perm}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-white rounded-lg border border-slate-200 p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Preferences</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="profile-display-name"
                className="block text-sm font-medium text-slate-700 mb-2"
              >
                Display name
              </label>
              <input
                id="profile-display-name"
                type="text"
                value={form.displayName}
                onChange={(e) => setForm((prev) => ({ ...prev, displayName: e.target.value }))}
                placeholder={profile.name ?? 'How you want to be addressed'}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength={120}
              />
              <p className="text-xs text-slate-500 mt-1">
                Shown in the chat header. Leave empty to use your Entra display name.
              </p>
            </div>

            <div>
              <label
                htmlFor="profile-language"
                className="block text-sm font-medium text-slate-700 mb-2"
              >
                Preferred language
              </label>
              <select
                id="profile-language"
                value={form.preferredLanguage}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, preferredLanguage: e.target.value }))
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.label} ({lang.code})
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Used for translated headings and number formatting.
              </p>
            </div>

            {canUseDefaultDatasource && (
              <div className="md:col-span-2">
                <label
                  htmlFor="profile-default-datasource"
                  className="block text-sm font-medium text-slate-700 mb-2"
                >
                  Default datasource
                </label>
                <select
                  id="profile-default-datasource"
                  value={form.defaultDatasourceId}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, defaultDatasourceId: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={NONE_DATASOURCE_VALUE}>None — choose per query</option>
                  {datasources.map((ds) => (
                    <option key={ds.datasource_id} value={ds.datasource_id}>
                      {ds.name} ({ds.type})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Pre-selects this datasource when you start a new query.
                </p>
              </div>
            )}
          </div>
        </section>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
        <button
          type="button"
          onClick={handleResetForm}
          disabled={isSaving || !isDirty}
          className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving || !isDirty}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          {isSaving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </div>
  );
};

interface ReadOnlyFieldProps {
  label: string;
  value: string;
  hint?: string;
}

const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({ label, value, hint }) => (
  <div className="grid grid-cols-3 items-start gap-2">
    <dt className="col-span-1 text-xs font-medium text-slate-500 uppercase tracking-wider mt-1">
      {label}
    </dt>
    <dd className="col-span-2">
      <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 break-all">
        {value}
      </div>
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </dd>
  </div>
);

function prettifyRole(role: string): string {
  switch (role) {
    case 'super_admin':
      return 'Super admin';
    case 'allocation_admin':
      return 'Allocation admin';
    case 'finance_admin':
      return 'Finance admin';
    case 'operations_admin':
      return 'Operations admin';
    case 'finance_user':
      return 'Finance user';
    case 'operations_user':
      return 'Operations user';
    case 'smart_insights_v3':
      return 'Smart Insights V3 user';
    default:
      return role;
  }
}

export default ComprehensiveProfileEditor;
